import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Legend,
} from 'recharts'
import { getDailyHistory, DailyHistoryPoint } from '../api'
import { Button } from '@/components/ui/button'
import { CalendarDays, TrendingUp } from 'lucide-react'

// Idea #96 (клиент Евгений, «История выполнений по дням»): сопоставляет
// по дням клики (OK/ошибки/капча) и СРЕДНЮЮ ПОЗИЦИЮ, чтобы искать
// закономерности: «в этот день клики просели — позиции тоже»,
// «удачная конфигурация → лиды/позиции выросли».
// Столбики = клики (левая ось), ЛИНИЯ = средняя позиция на ПРАВОЙ
// ИНВЕРТИРОВАННОЙ оси (позиция 1 сверху = лучше).
// Данные из почасовой статистики в БД → переживают перезапуск панели.

interface DailyHistoryChartProps {
  campaignId?: string
  mode?: 'booster' | 'profiler'
}

const PERIODS = [
  { days: 30, label: '30 дней' },
  { days: 60, label: '60 дней' },
  { days: 90, label: '90 дней' },
]

const C_OK = '#10B981'
const C_FAIL = '#EF4444'
const C_POS = '#f59e0b'

interface ChartPoint {
  date: string
  label: string
  ok: number
  fail: number
  captcha: number
  // null => нет данных о позиции в этот день (разрыв линии, не «позиция 0»)
  pos: number | null
}

function fmtDay(iso: string): string {
  // 'YYYY-MM-DD' → 'DD.MM'
  const p = iso.split('-')
  if (p.length === 3) return `${p[2]}.${p[1]}`
  return iso
}

function DailyTooltip({ active, payload, label }: any) {
  if (!active || !payload || payload.length === 0) return null
  const row: ChartPoint | undefined = payload[0]?.payload
  if (!row) return null
  return (
    <div className="rounded-lg border border-border bg-popover/95 backdrop-blur px-3 py-2 shadow-lg text-xs">
      <div className="font-medium text-foreground mb-1">{label}</div>
      <div className="flex flex-col gap-0.5 tabular-nums">
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full" style={{ background: C_OK }} />
          Успешно: <span className="font-semibold" style={{ color: C_OK }}>{row.ok}</span>
        </span>
        {row.fail > 0 && (
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full" style={{ background: C_FAIL }} />
            Ошибки: <span className="font-semibold" style={{ color: C_FAIL }}>{row.fail}</span>
          </span>
        )}
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full" style={{ background: C_POS }} />
          Ср. позиция: <span className="font-semibold" style={{ color: C_POS }}>
            {row.pos != null ? row.pos.toFixed(2) : '—'}
          </span>
        </span>
      </div>
    </div>
  )
}

export default function DailyHistoryChart({ campaignId, mode = 'booster' }: DailyHistoryChartProps) {
  const [items, setItems] = useState<DailyHistoryPoint[]>([])
  const [days, setDays] = useState(30)
  const [showFail, setShowFail] = useState(true)
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    if (!campaignId) { setItems([]); setLoading(false); return }
    setLoading(true)
    try {
      const res = await getDailyHistory(mode, campaignId, days)
      setItems(res.items || [])
    } catch { /* ignore */ }
    setLoading(false)
  }, [campaignId, mode, days])

  useEffect(() => { load() }, [load])

  const chartData: ChartPoint[] = useMemo(() => items.map(d => ({
    date: d.date,
    label: fmtDay(d.date),
    ok: d.ok,
    fail: d.fail,
    captcha: d.captcha,
    pos: d.avg_position > 0 ? d.avg_position : null,
  })), [items])

  const totals = useMemo(() => {
    let ok = 0, fail = 0
    let posW = 0, posN = 0
    for (const d of items) {
      ok += d.ok; fail += d.fail
      if (d.avg_position > 0 && d.ok > 0) { posW += d.avg_position * d.ok; posN += d.ok }
    }
    return { ok, fail, avgPos: posN > 0 ? posW / posN : 0, hasPos: posN > 0 }
  }, [items])

  // Recharts не подгоняет ширину оси под подписи: 4-значные значения
  // обрезались слева («1050» → «050»).
  const clicksAxisWidth = useMemo(() => {
    let max = 0
    for (const d of items) max = Math.max(max, d.ok, showFail ? d.fail : 0)
    const digits = String(Math.ceil(max * 1.1)).length
    return Math.max(28, digits * 7 + 12)
  }, [items, showFail])

  return (
    <div className="flex flex-col h-full">
      <div className="shrink-0 flex flex-wrap items-center gap-2 px-3 py-1.5 border-b border-border">
        <span className="flex items-center gap-1.5 text-xs font-medium text-foreground">
          <CalendarDays className="w-3.5 h-3.5" />
          История по дням
        </span>
        <div className="inline-flex items-center rounded-md border border-border overflow-hidden">
          {PERIODS.map((p, i) => (
            <button
              key={p.days}
              onClick={() => setDays(p.days)}
              className={`h-6 px-2 text-[11px] font-medium transition-colors cursor-pointer ${i > 0 ? 'border-l border-border' : ''} ${
                days === p.days ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <button
          onClick={() => setShowFail(v => !v)}
          className={`h-6 px-2 text-[11px] font-medium rounded-md border transition-colors cursor-pointer ${
            showFail ? 'border-red-500/40 text-red-500 bg-red-500/5' : 'border-border text-muted-foreground hover:text-foreground'
          }`}
          title="Показывать столбики ошибок"
        >
          Ошибки
        </button>
        {totals.ok + totals.fail > 0 && (
          <span className="ml-auto flex items-center gap-2 text-[11px] tabular-nums text-muted-foreground">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: C_OK }} />{totals.ok}</span>
            {totals.hasPos && (
              <span className="flex items-center gap-1"><TrendingUp className="w-3 h-3" style={{ color: C_POS }} />ср. {totals.avgPos.toFixed(1)}</span>
            )}
          </span>
        )}
        <Button variant="ghost" size="sm" onClick={load} className="h-6 text-xs gap-1 px-2">
          {'\u21BB'} Обновить
        </Button>
      </div>

      <div className="flex-1 min-h-0 p-3">
        {loading ? (
          <div className="flex items-center justify-center h-full text-muted-foreground text-sm">Загрузка...</div>
        ) : chartData.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-2 text-muted-foreground">
            <CalendarDays className="w-8 h-8 opacity-30" />
            <p className="text-sm">История появится по мере работы проекта</p>
            <p className="text-[11px] opacity-70">Данные копятся по дням и сохраняются между перезапусками</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%" minHeight={220}>
            <ComposedChart data={chartData} margin={{ top: 10, right: 6, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" strokeOpacity={0.35} vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }}
                tickLine={false}
                axisLine={false}
                minTickGap={16}
              />
              {/* Левая ось — клики */}
              <YAxis
                yAxisId="clicks"
                tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }}
                tickLine={false}
                axisLine={false}
                allowDecimals={false}
                width={clicksAxisWidth}
              />
              {/* Правая ось — средняя позиция, ИНВЕРТИРОВАННАЯ (1 сверху = лучше) */}
              <YAxis
                yAxisId="pos"
                orientation="right"
                reversed
                domain={[1, 'auto']}
                allowDecimals={false}
                tick={{ fontSize: 10, fill: C_POS }}
                tickLine={false}
                axisLine={false}
                width={30}
              />
              <Tooltip content={<DailyTooltip />} cursor={{ fill: 'var(--muted)', fillOpacity: 0.2 }} />
              <Legend
                wrapperStyle={{ fontSize: 11 }}
                formatter={(v) => v === 'ok' ? 'Успешно' : v === 'fail' ? 'Ошибки' : 'Ср. позиция'}
              />
              <Bar yAxisId="clicks" dataKey="ok" name="ok" fill={C_OK} radius={[3, 3, 0, 0]} maxBarSize={22} />
              {showFail && (
                <Bar yAxisId="clicks" dataKey="fail" name="fail" fill={C_FAIL} radius={[3, 3, 0, 0]} maxBarSize={22} />
              )}
              <Line
                yAxisId="pos"
                type="monotone"
                dataKey="pos"
                name="pos"
                stroke={C_POS}
                strokeWidth={2.25}
                dot={{ r: 2.5, fill: C_POS }}
                activeDot={{ r: 4 }}
                connectNulls={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  )
}
